import React, { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { Form, Input, notification, Row, Col, Button, Breadcrumb, Typography, Spin, DatePicker } from 'antd';
import {
  backButton,
  formItemLayout,
  handleBackendFormErrors,
  tailFormItemLayout,
  unexpectedError,
  formatDate,
  parseDate,
} from '../../../common_functions';

const HandWriting = props => {
  const { section, id } = useParams();
  const [handWritingNote, setHandwritingNote] = useState({});
  const [loading, setLoading] = useState(false);
  const formRef = useRef(null);
  useEffect(() => {
    async function fetchData() {
      try {
        setLoading(true);
        if (id !== 'new') {
          let { data } = await props.getItem('hand_writing_notes', id);
          data.begin_date = parseDate(data.begin_date);
          data.end_date = parseDate(data.end_date);
          setHandwritingNote(data);
          formRef.current.setFieldsValue(data);
        }
      } catch (error) {
        unexpectedError(error);
      } finally {
        setLoading(false);
      }
    }
    fetchData();
  }, [section, id]);

  let onFinish = async values => {
    try {
      setLoading(true);
      values.begin_date = formatDate(values.begin_date);
      values.end_date = formatDate(values.end_date);
      let newState = { ...handWritingNote, ...values };
      const response = await props.saveItem('hand_writing_notes', newState);
      if (response) {
        notification['success']({
          message: 'Handwriting saved successfully!',
          duration: 2,
        });
        backButton(props, `/settings/hand-writing`);
      }
    } catch (errorResponse) {
      handleBackendFormErrors(formRef, errorResponse);
    } finally {
      setLoading(false);
    }
  };

  const onFinishFailed = ({ errorFields }) => {
    formRef.current.scrollToField(errorFields[0].name);
  };
  // Custom validation function for secondary period begin
  const validateBeginDate = (_, value) => {
    // Get the period end date year value from the form
    const endDateYear = formRef.current.getFieldValue('end_date');
    if (endDateYear && value.isAfter(endDateYear)) {
      return Promise.reject(new Error('Begin date must be on or before the End date!'));
    }
    return Promise.resolve();
  };

  // Custom validation function for secondary period end
  const validateEndDate = (_, value) => {
    // Get the period start date year value from the form
    const startDateYear = formRef.current.getFieldValue('begin_date');
    if (startDateYear && value.isBefore(startDateYear)) {
      return Promise.reject(new Error('End date must be on or after the Begin date.'));
    }
    return Promise.resolve();
  };

  return (
    <Spin spinning={loading} size="large">
      <Row>
        <Col className="app-breadcrumb-col">
          <Breadcrumb separator="/" items={[{ title: "Settings", href: "/settings/hand-writing" }, { title: "Handwriting", href: "/settings/hand-writing" }, { title: id != 'new' ? `Edit Handwriting` : 'Add Handwriting' }]} />
        </Col>
        <Col className="white-paper">
          <Row gutter={24} className="page-header-section" align="middle">
            <Col xs={12}>
              <Typography.Title level={4} style={{ margin: 0 }}>
                {id != 'new' ? `Edit Handwriting: ${id}` : 'Add Handwriting'}
              </Typography.Title>
            </Col>
            <Col xs={12} justify="end"></Col>
          </Row>
          <hr />
          <div className="artist">
            <Form
              ref={formRef}
              layout="vertical"
              {...formItemLayout}
              onFinish={onFinish}
              onFinishFailed={onFinishFailed}
              initialValues={{ layout: tailFormItemLayout }}
            >
              <Row gutter={24}>
                <Col xs={24} sm={12} md={12} lg={12}>
                  <Form.Item
                    name="name"
                    label="Name"
                    rules={[
                      {
                        whitespace: true,
                        required: true,
                        message: 'Name is required!',
                      },
                    ]}
                  >
                    <Input placeholder="Enter Name" />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={12} md={12} lg={12}>
                  <Form.Item
                    name="begin_date"
                    label="Begin Date"
                    rules={[
                      {
                        validator: validateBeginDate,
                      },
                    ]}
                  >
                    <DatePicker picker="year" placeholder="Enter year" />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={12} md={12} lg={12}>
                  <Form.Item
                    name="end_date"
                    label="End Date"
                    rules={[
                      {
                        validator: validateEndDate, // Custom validation function
                      },
                    ]}
                  >
                    <DatePicker picker="year" placeholder="Enter year" />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={12} md={12} lg={12}>
                  <Form.Item
                    name="notes"
                    label="Notes"
                    rules={[
                      {
                        whitespace: true,
                        required: false,
                      },
                    ]}
                  >
                    <Input.TextArea placeholder="Enter notes" />
                  </Form.Item>
                </Col>
              </Row>
              <Form.Item {...tailFormItemLayout}>
                <Button
                  type="default"
                  htmlType="button"
                  onClick={() => backButton(props, '/settings/hand-writing')}
                  style={{
                    borderColor: '#d9d9d9',
                    background: '#f5f5f5',
                    color: 'rgba(0, 0, 0, 0.85)',
                  }}
                >
                  Back
                </Button>
                {props.showButton(props.roleType, props.tabs_permissions.settings_hand_writing, handWritingNote?.id ?? '')}
              </Form.Item>
            </Form>
          </div>
        </Col>
      </Row>
    </Spin>
  );
};

export default HandWriting;
